import { useState } from 'react';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchClient } from '@eureca/backoffice-api-sdk';
import {
  Badge,
  Button,
  Calendar,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Skeleton,
} from '@eureca/ui';
import { CalendarIcon } from '@eureca/ui/icons';
import { formatInstantDayMonthBR, formatInstantTimeBR } from '@/lib/format-date';

// ---------------------------------------------------------------------------
// Stack uptime — shape from backoffice BFF GET /system/uptime (Prometheus)
// ---------------------------------------------------------------------------

interface UptimeBucket {
  /** Início do dia, epoch em segundos. */
  t: number;
  /** Fração no ar no dia, 0..1. */
  uptime: number;
}

interface ServiceUptime {
  service: string;
  label: string;
  up: boolean | null;
  /** Média de disponibilidade nos últimos 30 dias, 0..1. */
  uptimeAvg: number | null;
  buckets: UptimeBucket[];
}

interface StackUptime {
  available: boolean;
  generatedAt: number;
  services: ServiceUptime[];
}

function useStackUptime(range: { from: string; to: string }) {
  return useQuery({
    queryKey: ['backoffice', 'system', 'uptime', range],
    queryFn: async () => {
      const res = await fetchClient<StackUptime>({
        method: 'GET',
        url: '/system/uptime',
        params: { from: range.from, to: range.to },
      });
      return res.data;
    },
    refetchInterval: 15_000,
  });
}

function formatPct(value: number | null): string {
  if (value == null) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

// --- date-range filter helpers (civil dates, YYYY-MM-DD) ---
function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return isoDate(d);
}

function parseIsoLocal(iso: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
}

/** Inclusive day count of the [from, to] range (min 1). */
function rangeDays(from: string, to: string): number {
  const a = parseIsoLocal(from)?.getTime();
  const b = parseIsoLocal(to)?.getTime();
  if (a == null || b == null) return 0;
  return Math.max(1, Math.round((b - a) / 86_400_000) + 1);
}

function uptimeTone(value: number | null): 'up' | 'warn' | 'down' | 'none' {
  if (value == null) return 'none';
  if (value >= 0.99) return 'up';
  if (value >= 0.5) return 'warn';
  return 'down';
}

const BAR_BG: Record<'up' | 'warn' | 'down' | 'none', string> = {
  up: 'bg-emerald-500',
  warn: 'bg-amber-500',
  down: 'bg-red-500',
  none: 'bg-muted-foreground/25',
};

function dayLabel(t: number): string {
  return formatInstantDayMonthBR(t * 1000);
}

/** Minutos estimados fora do ar no dia, derivados da fração de uptime. */
function downtimeMinutes(uptime: number): number {
  return Math.round((1 - uptime) * 24 * 60);
}

function statusWord(tone: 'up' | 'warn' | 'down' | 'none'): string {
  if (tone === 'up') return 'Estável';
  if (tone === 'warn') return 'Instável';
  if (tone === 'down') return 'Fora do ar';
  return 'Sem dados';
}

// ---------------------------------------------------------------------------
// Bar chart — one bar per day (last 30d), height ∝ uptime, hover detalha o dia
// ---------------------------------------------------------------------------

function UptimeBarChart({ buckets }: { buckets: UptimeBucket[] }) {
  const [hovered, setHovered] = useState<number | null>(null);

  if (buckets.length === 0) {
    return (
      <div className="flex h-16 items-center justify-center rounded-md bg-muted/30">
        <span className="text-[11px] text-muted-foreground">Sem amostras ainda</span>
      </div>
    );
  }

  return (
    <div
      className="flex h-16 items-stretch gap-[3px]"
      role="img"
      aria-label="Disponibilidade por dia nos últimos 30 dias"
    >
      {buckets.map((b, i) => {
        const tone = uptimeTone(b.uptime);
        const heightPct = Math.max(4, b.uptime * 100);
        const isHovered = hovered === i;
        const down = downtimeMinutes(b.uptime);
        return (
          <div
            key={b.t}
            className="relative flex flex-1 items-end"
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            {isHovered && (
              <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md border bg-popover px-2.5 py-1.5 shadow-md">
                <div className="text-[11px] font-medium text-popover-foreground">
                  {dayLabel(b.t)} · {statusWord(tone)}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {(b.uptime * 100).toFixed(1)}% no ar
                </div>
                {b.uptime < 1 && (
                  <div className="text-[11px] text-amber-600 dark:text-amber-400">
                    ~{down} min fora do ar
                  </div>
                )}
              </div>
            )}
            <div
              className={cn(
                'w-full rounded-t-[2px] transition-opacity',
                BAR_BG[tone],
                hovered !== null && !isHovered && 'opacity-40'
              )}
              style={{ height: `${heightPct}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Service card
// ---------------------------------------------------------------------------

function StatusDot({ up }: { up: boolean | null }) {
  return (
    <span
      className={cn(
        'inline-block size-2.5 rounded-full',
        up === true && 'bg-emerald-500',
        up === false && 'bg-red-500',
        up == null && 'bg-muted-foreground/40'
      )}
      aria-hidden="true"
    />
  );
}

function ServiceCard({ svc, days }: { svc: ServiceUptime; days: number }) {
  const statusLabel = svc.up === true ? 'No ar' : svc.up === false ? 'Fora do ar' : 'Sem dados';
  const tone = uptimeTone(svc.uptimeAvg);
  return (
    <div className="rounded-md border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <StatusDot up={svc.up} />
        <h3 className="text-sm font-medium text-foreground">{svc.label}</h3>
        <Badge
          variant={tone === 'down' ? 'destructive' : 'secondary'}
          className="ml-auto text-[11px]"
        >
          {formatPct(svc.uptimeAvg)} / {days}d
        </Badge>
      </div>
      <UptimeBarChart buckets={svc.buckets} />
      <p
        className={cn(
          'mt-2 text-xs',
          svc.up === true && 'text-emerald-600 dark:text-emerald-400',
          svc.up === false && 'text-destructive',
          svc.up == null && 'text-muted-foreground'
        )}
      >
        {statusLabel}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Service group (Candidato vs Compartilhados)
// ---------------------------------------------------------------------------

function ServiceGroup({
  title,
  services,
  days,
}: {
  title: string;
  services: ServiceUptime[];
  days: number;
}) {
  if (services.length === 0) return null;
  return (
    <div>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {services.map((svc) => (
          <ServiceCard key={svc.service} svc={svc} days={days} />
        ))}
      </div>
    </div>
  );
}

const CANDIDATE_SERVICES = new Set(['frontend', 'bff']);
const RECRUITER_SERVICES = new Set(['recruiter-frontend', 'recruiter-bff']);
/** Sobra (`core` + `keycloak`) é compartilhada entre todas as plataformas. */

// ---------------------------------------------------------------------------
// Legenda — explica o que cada elemento do painel significa
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type RangePreset = 'hoje' | '7d' | '30d' | 'custom';

const SIMPLE_PRESETS: { id: Exclude<RangePreset, 'custom'>; label: string }[] = [
  { id: 'hoje', label: 'Hoje' },
  { id: '7d', label: 'Últimos 7 dias' },
  { id: '30d', label: 'Últimos 30 dias' },
];

const MONTHS_PT_ABBR = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

/** Shadcn-style range label: "20 fev 2024". */
function fmtRangeDate(iso: string): string {
  const d = parseIsoLocal(iso);
  return d
    ? `${String(d.getDate()).padStart(2, '0')} ${MONTHS_PT_ABBR[d.getMonth()]} ${d.getFullYear()}`
    : '';
}

function resolveRange(
  preset: RangePreset,
  customFrom: string,
  customTo: string
): { from: string; to: string } {
  const today = isoDate(new Date());
  switch (preset) {
    case 'hoje':
      return { from: today, to: today };
    case '7d':
      return { from: isoDaysAgo(7), to: today };
    case '30d':
      return { from: isoDaysAgo(30), to: today };
    case 'custom':
      return { from: customFrom, to: customTo };
  }
}

function InicioPage() {
  const [preset, setPreset] = useState<RangePreset>('7d');
  const [customFrom, setCustomFrom] = useState(() => isoDaysAgo(7));
  const [customTo, setCustomTo] = useState(() => isoDate(new Date()));
  const [customOpen, setCustomOpen] = useState(false);
  const { from, to } = resolveRange(preset, customFrom, customTo);
  const { data, isLoading, isError } = useStackUptime({ from, to });
  const days = rangeDays(from, to);
  const customFromDate = parseIsoLocal(customFrom);
  const customToDate = parseIsoLocal(customTo);

  return (
    <div className="p-8">
      <h1 className="text-2xl font-semibold text-foreground">Bem-vindo ao Backoffice</h1>
      <p className="mt-2 text-muted-foreground">Plataforma de operações de suporte da Eureca.</p>

      {/* Filtro de período — presets + range específico (popover). Padrão: últimos 7 dias. */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {SIMPLE_PRESETS.map((p) => (
          <Button
            key={p.id}
            variant={preset === p.id ? 'default' : 'outline'}
            size="sm"
            aria-pressed={preset === p.id}
            onClick={() => setPreset(p.id)}
            data-testid={`inicio-filtro-${p.id}`}
          >
            {p.label}
          </Button>
        ))}

        <Popover open={customOpen} onOpenChange={setCustomOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={preset === 'custom' ? 'default' : 'outline'}
              size="sm"
              aria-pressed={preset === 'custom'}
              className="justify-start text-left font-normal"
              data-testid="inicio-filtro-custom"
            >
              <CalendarIcon className="mr-2 size-4" />
              {preset === 'custom'
                ? customToDate
                  ? `${fmtRangeDate(customFrom)} – ${fmtRangeDate(customTo)}`
                  : fmtRangeDate(customFrom)
                : 'Período específico'}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0">
            <Calendar
              mode="range"
              numberOfMonths={2}
              defaultMonth={customFromDate}
              selected={customFromDate ? { from: customFromDate, to: customToDate } : undefined}
              disabled={{ after: new Date() }}
              onSelect={(range) => {
                setPreset('custom');
                if (range?.from) setCustomFrom(isoDate(range.from));
                if (range?.to) {
                  setCustomTo(isoDate(range.to));
                  setCustomOpen(false);
                } else if (range?.from) {
                  setCustomTo(isoDate(range.from));
                }
              }}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="mt-8">
        <div className="mb-1 flex items-center gap-3">
          <h2 className="text-sm font-medium text-foreground">Saúde das plataformas</h2>
          {data && (
            <span className="text-xs text-muted-foreground">
              atualizado {formatInstantTimeBR(data.generatedAt * 1000)}
            </span>
          )}
        </div>
        <Separator className="mb-4" />

        {isLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((n) => (
              <Skeleton key={n} className="h-32 w-full" />
            ))}
          </div>
        )}

        {isError && (
          <p className="text-sm text-destructive">
            Não foi possível carregar a saúde das plataformas.
          </p>
        )}

        {data && !data.available && (
          <p className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground">
            Prometheus indisponível — sem dados de uptime. Verifique se o Prometheus (
            <code>docker compose up -d prometheus blackbox-exporter</code>) está no ar.
          </p>
        )}

        {data && data.available && (
          <div className="space-y-6">
            <ServiceGroup
              title="Compartilhados (todas as plataformas)"
              days={days}
              services={data.services.filter(
                (s) => !CANDIDATE_SERVICES.has(s.service) && !RECRUITER_SERVICES.has(s.service)
              )}
            />
            <ServiceGroup
              title="Candidato"
              days={days}
              services={data.services.filter((s) => CANDIDATE_SERVICES.has(s.service))}
            />
            <ServiceGroup
              title="Consultor"
              days={days}
              services={data.services.filter((s) => RECRUITER_SERVICES.has(s.service))}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_auth/_shell/inicio')({
  component: InicioPage,
});
